import { BaseError, type BaseErrorOptions } from '@canvas/error';

import { type MutationResult, type QueryResult } from './graphql/types';

export class GraphQLClientError extends BaseError {
  constructor(message: string, options?: BaseErrorOptions) {
    super({ name: 'GraphQLClientError', ...options }, message);
  }
}

/**
 * Throws an error if the response contains errors or is empty
 * and will throw the custom error if provided.
 *
 * @param responseOrErr
 * @param customError
 */
export function throwOnQueryError<T>(
  responseOrErr: QueryResult<T> | Error,
  customError?: GraphQLClientError,
): asserts responseOrErr is QueryResult<T> & { data: T } {
  if (responseOrErr instanceof Error) {
    if (customError && responseOrErr instanceof GraphQLClientError) {
      throw customError;
    }

    throw responseOrErr;
  }

  if (responseOrErr.errors && responseOrErr.errors.length > 0) {
    throw new GraphQLClientError(
      `graphql response contained errors: ${JSON.stringify(responseOrErr.errors)}`,
    );
  }

  if (!responseOrErr.data) {
    throw new GraphQLClientError(
      `graphql empty response, errors: ${JSON.stringify(responseOrErr.errors)}`,
    );
  }
}

/**
 * Throws an error if the response contains errors or is empty
 * and will throw the custom error if provided.
 *
 * @param responseOrErr
 * @param customError
 */
export function throwOnMutationError<T>(
  responseOrErr: MutationResult<T> | Error,
  customError?: GraphQLClientError,
): asserts responseOrErr is MutationResult<T> & { data: T } {
  if (responseOrErr instanceof Error) {
    if (customError && responseOrErr instanceof GraphQLClientError) {
      throw customError;
    }

    throw responseOrErr;
  }

  if (responseOrErr.errors && responseOrErr.errors.length > 0) {
    throw new GraphQLClientError(
      `graphql response contained errors: ${JSON.stringify(responseOrErr.errors)}`,
    );
  }

  if (!responseOrErr.data) {
    throw new GraphQLClientError(
      `graphql empty response, errors: ${JSON.stringify(responseOrErr.errors)}`,
    );
  }
}
